import type { CSSProperties, ReactNode } from "react";
import { healthToneKey } from "../health";
import type { PlatformHealthVerdict } from "../types";

const TONE_COLOR: Record<ReturnType<typeof healthToneKey>, string> = {
  success: "#6ee7b7",
  warning: "#fcd34d",
  danger: "#fda4af",
  neutral: "#94a3b8",
};

export type PlatformHealthPanelProps = {
  verdict: PlatformHealthVerdict;
  className?: string;
  style?: CSSProperties;
  /** Compact: verdict only; expanded shows reason + evidence. */
  compact?: boolean;
};

/**
 * Platform Health with reason + evidence — one composition, no mental correlation.
 */
export function PlatformHealthPanel({
  verdict,
  className = "",
  style,
  compact = false,
}: PlatformHealthPanelProps): ReactNode {
  const tone = healthToneKey(verdict.label);
  const color = TONE_COLOR[tone];

  return (
    <div
      className={`platform-health-panel platform-health-panel--${tone} ${className}`.trim()}
      style={style}
      data-health={verdict.label}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0.5rem" }}>
        <span style={{ fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>
          Platform Health
        </span>
        <strong style={{ color, fontSize: "0.95rem" }}>{verdict.label}</strong>
      </div>
      {!compact && verdict.reason ? (
        <p style={{ margin: "0.4rem 0 0", fontSize: "0.8rem", color: "#cbd5e1" }}>
          <span style={{ color: "#94a3b8" }}>Reason: </span>
          {verdict.reason}
        </p>
      ) : null}
      {!compact && verdict.evidence ? (
        <p style={{ margin: "0.25rem 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
          <span>Evidence: </span>
          {verdict.evidence}
        </p>
      ) : null}
    </div>
  );
}
